import React, { useState, useEffect, useContext, useMemo } from "react"
import { get, sortBy, reverse, pick, isEmpty } from "lodash"
import ClassNames from "classnames"

import { infoTableColumns } from "config/table_columns"
import { APIResponse } from "redux/apis/types"
import { InfoTable, InfoTableProps } from "components/molecules/table"
import * as Formatter from "utils/Formatter"
import { SymbolDetailModalNewsPanel } from "components/organisms/panel/newsList"
import { checkIsSp } from "utils/util"
import { SymbolDetailContext } from "components/pages/others/symbolDetail"
import APIs from "redux/apis"
import { useSelector } from "react-redux"
import { LoadStateType, pageLoadRegist } from "redux/ducks/PageLoad"
import store from "../../../../redux/store"
import { refreshSymbol } from "../../../../redux/ducks/SymbolDetail"
import uuid from "uuid/v4"

type DetailPropsType = {
  symbolDetail: APIResponse.Res
}

type TableListType = Array<{ name: string; table: string }>
const tableList: Array<TableListType> = [
  [{ name: "前場", table: "before" }, { name: "基本情報", table: "unit" }],
  [{ name: "後場", table: "after" }, { name: "株価位置", table: "position" }],
]

const spTableList: Array<TableListType> = [
  [
    { name: "前場", table: "before" },
    { name: "後場", table: "after" },
    { name: "", table: "unit" },
    { name: "株価位置", table: "position" },
  ],
]

const stockPositionTableListContent = [
  {
    key: "YearlyHighPrice",
    title: "年初来高値",
    value: (data: object) => (
      <span>
        {Formatter.number(
          get(data, "YearlyHighPrice"),
          get(data, "NumberOfDecimals")
        )}
      </span>
    ),
    unit: (data: object) => (
      <span className="time-col stock-position-table-unit">
        {Formatter.date(get(data, "YearlyHighDay"), "YYYY/MM/DD")}
      </span>
    ),
  },
  {
    key: "YearlyLowPrice",
    title: "年初来安値",
    value: (data: object) => (
      <span>
        {Formatter.number(
          get(data, "YearlyLowPrice"),
          get(data, "NumberOfDecimals")
        )}
      </span>
    ),
    unit: (data: object) => (
      <span className="time-col stock-position-table-unit">
        {Formatter.date(get(data, "YearlyLowDay"), "YYYY/MM/DD")}
      </span>
    ),
  },
  {
    key: "ListedHighPrice",
    title: "上場来高値",
    value: (data: object) => (
      <span>
        {Formatter.number(
          get(data, "ListedHighPrice"),
          get(data, "NumberOfDecimals")
        )}
      </span>
    ),
    unit: (data: object) => (
      <span className="time-col stock-position-table-unit">
        {Formatter.date(get(data, "ListedHighDay"), "YYYY/MM/DD")}
      </span>
    ),
  },
  {
    key: "ListedLowPrice",
    title: "上場来安値",
    value: (data: object) => (
      <span>
        {Formatter.number(
          get(data, "ListedLowPrice"),
          get(data, "NumberOfDecimals")
        )}
      </span>
    ),
    unit: (data: object) => (
      <span className="time-col stock-position-table-unit">
        {Formatter.date(get(data, "ListedLowDay"), "YYYY/MM/DD")}
      </span>
    ),
  },
  {
    key: "MA5",
    title: "5日移動平均",
    value: (data: object) => (
      <span className="date-none">
        {Formatter.number(get(data, "MA5"), get(data, "NumberOfDecimals"))}
      </span>
    ),
    unit: () => <span className="stock-position-table-unit" />,
  },
  {
    key: "MA25",
    title: "25日移動平均",
    value: (data: object) => (
      <span className="date-none">
        {Formatter.number(get(data, "MA25"), get(data, "NumberOfDecimals"))}
      </span>
    ),
    unit: () => <span className="stock-position-table-unit" />,
  },
  {
    key: "Price",
    title: "現在値",
    value: (data: object) => (
      <span>
        {Formatter.number(get(data, "Price"), get(data, "NumberOfDecimals"))}
      </span>
    ),
    unit: (data: object) => (
      <span className="time-col table-unit-space-time">
        {Formatter.date(get(data, "PriceTime"), "HH:mm")}
      </span>
    ),
  },
]

const StockPositionTable: React.FC<
  InfoTableProps & { list?: null }
> = props => {
  const stockPositionTableList = {
    titleCls: "title-cell",
    bodyCls: "index-detail-cell number-cell",
    list: [
      reverse(
        sortBy(stockPositionTableListContent, o =>
          get(props.data, get(o, "key"))
        )
      ),
    ],
  }

  return (
    <div className="table-position-container">
      <div className="table-position-marker" />
      <InfoTable
        list={stockPositionTableList}
        data={props.data}
        className={props.className}
        wrapperCls={props.wrapperCls}
      />
    </div>
  )
}

export const Detail: React.FC<DetailPropsType> = props => {
  const [data, setData] = useState({})
  const symbolData = get(props.symbolDetail, "Symbol")
  const isSp = checkIsSp()
  const symbolDetailContext = useContext(SymbolDetailContext)
  const { flag, setFlag } = symbolDetailContext
  const isRefresh = useSelector(
    (state: { symbolDetail: { refresh: boolean } }) =>
      state.symbolDetail.refresh
  )
  const loadingId = useMemo(() => uuid(), [])

  const callSymbolDetailApi = async () => {
    store.dispatch(pageLoadRegist(loadingId, true))
    const _data = await APIs.SymbolDetail.SymbolDetail(
      pick(symbolData, ["Symbol", "SecurityType", "Exchange"])
    )
    if (!!get(_data, "isAxiosError")) {
      setData({})
    } else {
      setData(get(_data, "Symbol"))
    }
    store.dispatch(pageLoadRegist(loadingId, false))
  }

  useEffect(() => {
    // flag === 1 <=すでにデータを親から受け取り済み
    // Apiコールでデータを取得する。
    if (flag === 1) {
      callSymbolDetailApi()
      return
    }
    // flag === 0 <=データを親からまだ受け取っていない状態。
    // symbolDetailはApiの戻り値なので、初回レンダリング時には空になっている（初回レンダリングとApiのpendding時間は初回レンダリングのほうが早いため）。
    // そのため、flag === 0 かつ　symbolDetailが空でない場合、データを受け取りflagを1にする。
    if (flag === 0) {
      if (isEmpty(symbolData)) return
      setData(symbolData)
      setFlag(1)
    }
  }, [props.symbolDetail])

  useEffect(() => {
    isRefresh && store.dispatch(refreshSymbol())
  }, [isRefresh])

  const MappedTable: React.FC<{ list: TableListType }> = props => {
    return (
      <div className="panel-splits panel-split-price-col">
        {props.list.map((info, i) => (
          <div key={i} className="title-table-container">
            {info.name ? <div className="title-border">{info.name}</div> : null}
            {info.table === "position" ? (
              <StockPositionTable
                key={i}
                list={null}
                data={data}
                className="table-striped table-index-detail"
                wrapperCls="index-detail-table-row"
              />
            ) : (
              <InfoTable
                key={i}
                list={get(infoTableColumns.symbolDetail.detail, info.table)}
                data={data}
                className="table-striped table-index-detail"
                wrapperCls="index-detail-table-row"
              />
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="detail-contents">
      <div className={ClassNames({ "split-division": !isSp })}>
        {(isSp ? spTableList : tableList).map((list, key: number) => (
          <MappedTable key={key} list={list} />
        ))}
      </div>
      {!isSp && (
        <SymbolDetailModalNewsPanel symbolCode={get(symbolData, "Symbol")} />
      )}
    </div>
  )
}
